import React from "react";
import { Box, Grid, ButtonGroup, Button } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import useRevenueAnalytics from "../hooks/useRevenueAnalytics";
import FiltersBar from "../components/analytics/FiltersBar";
import RevenueTrendChart from "../components/analytics/RevenueTrendChart";
import PaymentList from "../components/analytics/PaymentList";
import LeaderboardList from "../components/analytics/LeaderboardList";
import CustomerInvoicesCard from "../components/analytics/CustomerInvoicesCard";
import FlaggedInvoicesCard from "../components/analytics/FlaggedInvoicesCard";
import DevErrorBoundary from "../components/DevErrorBoundary";

const currencyFmt = (v, code = "USD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency: code, maximumFractionDigits: 0 }).format(v || 0);
const getLocalDateRangeISO = (fromDate, toDate) => {
  const start = new Date(`${fromDate}T00:00:00`);
  const endExclusive = new Date(`${toDate}T00:00:00`);
  endExclusive.setDate(endExclusive.getDate() + 1);
  return { fromISO: start.toISOString(), toISO: endExclusive.toISOString() };
};

export default function RevenueDashboardPage({ endpoint='/api/analytics/revenue', currency='USD' }) {
  const history = useHistory();
  const [mode, setMode] = React.useState('cash');
  const [accrualField, setAccrualField] = React.useState('receivedByDate');
  const [rangePreset, setRangePreset] = React.useState('30d');
  const [useCustom, setUseCustom] = React.useState(false);
  const [fromCustom, setFromCustom] = React.useState('');
  const [toCustom, setToCustom] = React.useState('');
  const [statusIn, setStatusIn] = React.useState(['Paid','Partial']);
  const [topProductMetric, setTopProductMetric] = React.useState('revenue');

  const now = new Date();
  const { fromISO, toISO, grp } = React.useMemo(() => {
    if (useCustom && fromCustom && toCustom) {
      const isYTD = new Date(`${fromCustom}T00:00:00`).toDateString() === new Date(new Date().getFullYear(), 0, 1).toDateString();
      return { ...getLocalDateRangeISO(fromCustom, toCustom), grp: isYTD ? 'month' : 'day' };
    }
    let start;
    if (rangePreset === '30d') start = new Date(now.getTime() - 29*864e5);
    else if (rangePreset === '90d') start = new Date(now.getTime() - 89*864e5);
    else start = new Date(now.getFullYear(), 0, 1);
    return { fromISO: start.toISOString(), toISO: new Date(now.getTime()+1).toISOString(), grp: rangePreset === 'ytd' ? 'month' : 'day' };
  }, [useCustom, fromCustom, toCustom, rangePreset]);

  const { data, loading, error } = useRevenueAnalytics({
    endpoint, mode, accrualField, fromISO, toISO, grp, statusIn
  });

  const series = (data?.series ?? []).map(d => ({ ...d, period: new Date(d.period) }));
  const paymentsByInvoice = data?.paymentsByInvoice ?? [];
  const accrualInvoices = data?.accrualInvoices ?? [];
  const timelineEntries = mode === 'accrual' ? accrualInvoices : paymentsByInvoice;

  const topProductsRows = React.useMemo(() => {
    const list = (data?.topProducts ?? []).map(p => ({
      name: p.name || p._id,
      id: p._id,
      revenue: Number(p.revenue) || 0,
      qty: Number(p.qty) || 0,
      profit: typeof p.margin === 'number' ? p.margin : 0,
      margin: p.margin,
    }));
    const metricKey = topProductMetric === 'quantity' ? 'qty' : topProductMetric === 'profit' ? 'profit' : 'revenue';
    return list
      .sort((a, b) => (b[metricKey] || 0) - (a[metricKey] || 0))
      .map(item => ({
        left: item.name,
        right: metricKey === 'qty' ? Math.round(item.qty) : item[metricKey],
        sub: `Qty ${Math.round(item.qty)}${typeof item.margin === 'number' ? ` • GM ${currencyFmt(item.margin, currency)}` : ''}`,
        meta: { modelNumber: item.id },
      }));
  }, [data?.topProducts, topProductMetric, currency]);
  const topCustomersRows = (data?.topCustomers ?? []).map((c) => {
    const locDetails = (c.locations || [])
      .filter((l) => l?.location)
      .map((l) => `${l.location}: ${currencyFmt(l.revenue || 0, currency)}`)
      .join(" • ");
    return {
      left: c._id || "Unknown account",
      right: c.revenue,
      sub: locDetails,
    };
  });

  const handleProductRowClick = React.useCallback(
    (row) => {
      const model = row?.meta?.modelNumber || row?.left;
      if (!model) return;
      history.push(`/products?focus=${encodeURIComponent(model)}`);
    },
    [history]
  );

  return (
    <DevErrorBoundary>
      <Box display="grid" gridGap={16}>
      <FiltersBar
        mode={mode} setMode={setMode}
        accrualField={accrualField} setAccrualField={setAccrualField}
        rangePreset={rangePreset} setRangePreset={setRangePreset}
        useCustom={useCustom} setUseCustom={setUseCustom}
        fromCustom={fromCustom} setFromCustom={setFromCustom}
        toCustom={toCustom} setToCustom={setToCustom}
        statusIn={statusIn} setStatusIn={setStatusIn}
        loading={loading}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <RevenueTrendChart
            subheader={`Grouped by ${grp}${error ? ' • ' + error : ''}`}
            series={series}
            currency={currency}
            totalRevenue={data?.kpis?.gross || 0}
            mode={mode}
            accrualField={accrualField}
            grouping={grp}
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <PaymentList
            entries={timelineEntries}
            currency={currency}
            mode={mode}
            title={mode === 'accrual' ? 'Accrued Invoices' : 'Displayed Payments'}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <LeaderboardList
            title="Top 50 Products"
            rows={topProductsRows}
            currency={currency}
            onRowClick={handleProductRowClick}
            maxRows={50}
            scrollHeight={420}
            money={topProductMetric !== 'quantity'}
            headerAction={
              <ButtonGroup size="small" variant="outlined">
                <Button onClick={() => setTopProductMetric('revenue')} variant={topProductMetric === 'revenue' ? 'contained' : 'outlined'}>Revenue</Button>
                <Button onClick={() => setTopProductMetric('quantity')} variant={topProductMetric === 'quantity' ? 'contained' : 'outlined'}>Quantity</Button>
                <Button onClick={() => setTopProductMetric('profit')} variant={topProductMetric === 'profit' ? 'contained' : 'outlined'}>Profit</Button>
              </ButtonGroup>
            }
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <LeaderboardList title="Top 50 Customers" rows={topCustomersRows} currency={currency} maxRows={50} scrollHeight={420} />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <CustomerInvoicesCard currency={currency} />
        </Grid>
        <Grid item xs={12} md={6}>
          <FlaggedInvoicesCard currency={currency} />
        </Grid>
      </Grid>
    </Box>
    </DevErrorBoundary>
  );
}
